import { Table } from '@tanstack/react-table';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

function getPageNumbers(
  current: number,
  total: number,
): (number | 'ellipsis')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];
  if (current > 3) pages.push('ellipsis');

  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    pages.push(i);
  }

  if (current < total - 2) pages.push('ellipsis');
  pages.push(total);

  return pages;
}

interface DataTableNumberedPaginationProps<TData> {
  table: Table<TData>;
}

export function DataTableNumberedPagination<TData>({
  table,
}: DataTableNumberedPaginationProps<TData>) {
  const totalPages = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;

  if (totalPages <= 1) return null;

  const setCurrentPage = (page: number) => table.setPageIndex(page - 1);
  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => setCurrentPage(currentPage - 1)}
            aria-disabled={currentPage === 1}
            className={
              currentPage === 1
                ? 'tw:pointer-events-none tw:opacity-50 tw:no-underline!'
                : 'tw:no-underline!'
            }
          />
        </PaginationItem>
        {pages.map((page, i) =>
          page === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <PaginationLink
                isActive={page === currentPage}
                onClick={() => setCurrentPage(page)}
                className="tw:no-underline!"
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            onClick={() => setCurrentPage(currentPage + 1)}
            aria-disabled={currentPage === totalPages}
            className={
              currentPage === totalPages
                ? 'tw:pointer-events-none tw:opacity-50 tw:no-underline!'
                : 'tw:no-underline!'
            }
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
